import { useEffect, useRef, useState } from "react";
import { Menu, X, Truck, ChevronDown } from "lucide-react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { BRAND } from "@/data/content";
import { trackEvent } from "@/lib/tracking";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const location = useLocation();
  const toggleRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenDesktopMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const previous = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
    Object.assign(document.body.style, { position: "fixed", top: `-${scrollY}px`, width: "100%", overflow: "hidden" });
    window.dispatchEvent(new CustomEvent("ranger-menu", { detail: true }));
    const onKey = (event) => {
      if (event.key === "Escape") { setOpen(false); toggleRef.current?.focus(); }
      if (event.key === "Tab") {
        const links = [...drawerRef.current.querySelectorAll("a[href]")];
        const first = toggleRef.current;
        const last = links[links.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    const desktop = window.matchMedia("(min-width: 1280px)");
    const onResize = () => { if (desktop.matches) setOpen(false); };
    document.addEventListener("keydown", onKey);
    desktop.addEventListener("change", onResize);
    return () => {
      Object.assign(document.body.style, previous);
      window.dispatchEvent(new CustomEvent("ranger-menu", { detail: false }));
      window.scrollTo(0, scrollY);
      document.removeEventListener("keydown", onKey);
      desktop.removeEventListener("change", onResize);
    };
  }, [open]);

  // Solid (light glass) nav on inner pages or when scrolled; transparent over the home hero at the top.
  const solid = scrolled || location.pathname !== "/";

  const handleClick = (label) => trackEvent("nav_click", { label });

  // Keep the desktop header compact while making every public hub and supporting
  // page reachable from the primary navigation.
  const desktopNavigation = [
    { key: "home", label: "Home", path: "/" },
    {
      key: "about",
      label: "About",
      path: "/about/company-history",
      items: [
        { label: "Company Overview & History", path: "/about/company-history" },
        { label: "Fleet & Technology", path: "/about/fleet-technology" },
        { label: "Vecellio Group Legacy", path: "/about/vecellio-group" },
        { label: "Community Outreach", path: "/about/community" },
        { label: "Awards & Recognition", path: "/about/awards" },
      ],
    },
    {
      key: "services", label: "Services", path: "/services",
      items: [
        { label: "Heavy Highway & Infrastructure", path: "/services/heavy-highway-infrastructure" },
        { label: "Site Work & Commercial", path: "/services/site-work-commercial" },
        { label: "Industrial & Specialty", path: "/services/industrial-specialty" },
        { label: "Materials & Plant Sales", path: "/services/materials-plant-sales" },
      ],
    },
    {
      key: "projects",
      label: "Projects",
      path: "/projects",
      items: [
        { label: "All Projects", path: "/projects" },
        { label: "Filter by Market", path: "/projects/markets" },
      ],
    },
    {
      key: "safety",
      label: "Safety",
      path: "/safety",
      items: [
        { label: "Safety Culture & Zero-Incident Focus", path: "/safety" },
        { label: "Safety Certifications", path: "/safety/certifications" },
        { label: "Safety Awards", path: "/safety/awards" },
      ],
    },
    {
      key: "asphalt",
      label: "FOB Asphalt Sales",
      path: "/asphalt-plants",
      items: [
        { label: "Plant Locations", path: "/asphalt-plants/locations" },
        { label: "Quality Control & Materials Testing", path: "/asphalt-plants/quality-control-labs" },
        { label: "FOB Mixes & Specifications", path: "/asphalt-plants/fob-mix-designs" },
        { label: "Dispatch & Orders", path: "/asphalt-plants/order-dispatch" },
      ],
    },
    {
      key: "careers",
      label: "Careers",
      path: "/careers",
      items: [
        { label: "Why Work at Ranger", path: "/careers/culture-benefits" },
        { label: "Current Job Openings", path: "/careers/open-positions" },
      ],
    },
    {
      key: "news",
      label: "News",
      path: "/news",
      items: [
        { label: "Latest Press & Project Updates", path: "/news" },
        { label: "The VanGuard News Magazine", path: "/news/vanguard-magazine" },
      ],
    },
    {
      key: "contact",
      label: "Contact",
      path: "/contact",
      items: [
        { label: "Contact Us", path: "/contact" },
        { label: "Office & Plant Directory", path: "/asphalt-plants/locations" },
        { label: "Bid Request / RFP Submission Form", path: "/contact/request-a-bid" },
      ],
    },
  ].sort((a, b) => ["home", "about", "services", "asphalt", "safety", "projects", "news", "careers", "contact"].indexOf(a.key) - ["home", "about", "services", "asphalt", "safety", "projects", "news", "careers", "contact"].indexOf(b.key));

  const linkClass = ({ isActive }) =>
    `font-semibold text-[12px] uppercase tracking-[0.08em] transition-colors whitespace-nowrap ${
      isActive ? "text-rc-red" : solid ? "text-rc-charcoal hover:text-rc-red" : "text-white/90 hover:text-rc-sand"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "backdrop-blur-xl bg-rc-bg/90 border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" aria-label="Ranger Construction home" className="mr-2 xl:mr-4 flex shrink-0 items-center">
          <img src={BRAND.logo} alt="Ranger Construction" className="block h-8 sm:h-10 w-auto shrink-0" />
        </Link>

        <nav className="hidden xl:flex items-center gap-3" aria-label="Primary navigation">
          {desktopNavigation.map((n) => n.items ? (
            <div key={n.key} className="relative">
              <button
                type="button"
                data-testid={`nav-${n.key}-menu`}
                aria-expanded={openDesktopMenu === n.key}
                aria-haspopup="menu"
                onClick={() => setOpenDesktopMenu(openDesktopMenu === n.key ? null : n.key)}
                className={`${linkClass({ isActive: location.pathname === n.path || location.pathname.startsWith(`${n.path}/`) })} inline-flex items-center gap-1 py-7`}
              >
                {n.label} <ChevronDown size={14} aria-hidden="true" />
              </button>
              {openDesktopMenu === n.key && (
                <div role="menu" className="absolute left-0 top-full w-64 rounded-lg border border-black/10 bg-rc-bg py-2 shadow-xl">
                  {n.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      role="menuitem"
                      onClick={() => handleClick(item.label)}
                      className={({ isActive }) => `block px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-rc-red/10 text-rc-red" : "text-rc-charcoal hover:bg-black/5 hover:text-rc-red"}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={n.key}
              to={n.path}
              data-testid={`nav-${n.key}-link`}
              onClick={() => handleClick(n.label)}
              className={linkClass}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto mr-3 xl:ml-4 xl:mr-0 flex items-center shrink-0">
          <Link
            to="/asphalt-plants/order-dispatch"
            data-testid="nav-dispatch-button"
            onClick={() => handleClick("Dispatch & Orders")}
            className="inline-flex items-center gap-2 bg-rc-red text-white px-3 xl:px-4 py-3 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wide hover:bg-rc-reddark transition-colors whitespace-nowrap"
          >
            <Truck size={14} className="hidden sm:block" /> Dispatch & Orders
          </Link>
        </div>

        <button
          ref={toggleRef}
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          className={`xl:hidden ${solid ? "text-rc-charcoal" : "text-white"}`}
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div ref={drawerRef} id="mobile-navigation" data-lenis-prevent className="xl:hidden bg-rc-bg border-t border-black/10 px-5 py-4 flex flex-col gap-1 overflow-y-auto overscroll-contain max-h-[calc(100dvh-5rem)]">
          {desktopNavigation.map((n) => n.items ? (
            <div key={n.key} className="border-b border-black/5 py-3">
              {n.key === "services" ? (
                <div data-testid={`mobile-nav-${n.key}`} className="block font-semibold uppercase tracking-wide text-sm text-rc-charcoal">
                  {n.label}
                </div>
              ) : (
                <div data-testid={`mobile-nav-${n.key}`} className="block font-semibold uppercase tracking-wide text-sm text-rc-charcoal">
                  {n.label}
                </div>
              )}
              <div className="mt-2 ml-3 grid gap-1 border-l border-rc-red/30 pl-3">
                {n.items.map((item) => (
                  <NavLink key={item.path} to={item.path} className={({ isActive }) => `py-1.5 text-sm ${isActive ? "text-rc-red font-semibold" : "text-rc-brown"}`}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink
              key={n.key}
              to={n.path}
              data-testid={`mobile-nav-${n.key}`}
              className={({ isActive }) => `text-left py-3 font-semibold uppercase tracking-wide text-sm border-b border-black/5 ${isActive ? "text-rc-red" : "text-rc-charcoal"}`}
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/asphalt-plants/order-dispatch"
            data-testid="mobile-dispatch-button"
            className="mt-3 inline-flex items-center justify-center gap-2 bg-rc-red text-white px-6 py-3 rounded-full text-xs font-bold uppercase tracking-widest text-center"
          >
            <Truck size={14} /> Dispatch & Orders
          </Link>
        </div>
      )}
    </header>
  );
}
